import { GetStaticPropsResult } from 'next'

import { SanitySiteFragment } from '@data/sanity/queries/types/site'
import { SanityAccountPage } from '@data/sanity/queries/types/page'
import { SanityProductCatalogueQuery } from '@data/sanity/queries/types/product'
import { getPreview } from '@lib/sanity/client'
import { getAccountPage } from '@lib/sanity/page'
import { getProductCatalogue } from '@lib/sanity/product'
import { useUser } from '@lib/auth'
import { Locale } from '@lib/language'
import { CustomStaticPropsContext, getPageUrl, PageType } from '@lib/routes'

import Modules from '@modules/modules'
import Layout from '@components/layout'

interface AccountPageProps {
  locale: Locale
  page: SanityAccountPage
  site: SanitySiteFragment
  productCatalogue: SanityProductCatalogueQuery
}

const AccountPage = ({ page, site }: AccountPageProps) => {
  // Redirect to login page if not logged in
  const { user } = useUser(getPageUrl(PageType.LOGIN_PAGE))

  if (!user?.isLoggedIn) {
    return null
  }

  return (
    <Layout page={page} site={site}>
      <Modules modules={page.modules} />
    </Layout>
  )
}

export async function getStaticProps({
  locale,
  preview,
  previewData,
}: CustomStaticPropsContext): Promise<GetStaticPropsResult<AccountPageProps>> {
  const accountPage = await getAccountPage(
    locale,
    getPreview(preview, previewData)
  )

  if (!accountPage.page) {
    return { notFound: true }
  }

  const productCatalogue = await getProductCatalogue(locale)

  return {
    props: {
      locale,
      page: accountPage.page,
      site: accountPage.site,
      productCatalogue,
    },
    revalidate: 60,
  }
}

export default AccountPage
